import { Component, Inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { CustomWidgetFullscreenChartMode, CustomWidgetModel } from "app/models/customWidget.model";
import { DataTypeModelKey, isRetiredDataTypeModelKey } from "app/models/dataType.model";
import { ProjectDeviceModel } from "app/models/projectDevice.model";

type CustomWidgetEditorData = {
    devices: ProjectDeviceModel[];
    widget?: CustomWidgetModel | null;
};

const invalidCustomWidgetMetrics = new Set<DataTypeModelKey>([
    DataTypeModelKey.overload,
    DataTypeModelKey.underrange,
    DataTypeModelKey.spectrum13Octave,
    DataTypeModelKey.spectrum13Octave1Minute,
    DataTypeModelKey.onlineDevices,
    DataTypeModelKey.eventStream,
]);

@Component({
    selector: "app-custom-widget-editor",
    templateUrl: "./custom-widget-editor.component.html",
    styleUrls: ["./custom-widget-editor.component.scss"],
    standalone: false,
})
export class CustomWidgetEditorComponent {
    readonly allMetricOptions: DataTypeModelKey[] = Object.values(DataTypeModelKey).filter(
        (metric) =>
            !invalidCustomWidgetMetrics.has(metric) &&
            !isRetiredDataTypeModelKey(metric),
    );

    readonly fullscreenModes = [
        { value: CustomWidgetFullscreenChartMode.show, label: "Show chart in kiosk" },
        { value: CustomWidgetFullscreenChartMode.hide, label: "Hide chart in kiosk" },
    ];

    readonly widget: CustomWidgetModel;

    constructor(
        private readonly dialogRef: MatDialogRef<CustomWidgetEditorComponent>,
        @Inject(MAT_DIALOG_DATA) public readonly data: CustomWidgetEditorData,
    ) {
        this.widget = {
            id: data.widget?.id || "",
            name: data.widget?.name || "",
            projectDeviceId: data.widget?.projectDeviceId || data.devices[0]?.id || "",
            primaryMetrics: [...(data.widget?.primaryMetrics || [DataTypeModelKey.soundPressureLevel])],
            secondaryMetrics: [...(data.widget?.secondaryMetrics || [])],
            chartMetric: data.widget?.chartMetric ?? null,
            fullscreenChartMode: data.widget?.fullscreenChartMode || CustomWidgetFullscreenChartMode.show,
        };
    }

    get title(): string {
        return this.widget.id ? "Edit custom widget" : "Add custom widget";
    }

    get metricOptions(): DataTypeModelKey[] {
        const device = this.selectedDevice;
        const availableFields = device?.fields || [];

        if (!availableFields.length) {
            return this.allMetricOptions;
        }

        const availableMetrics = new Set<DataTypeModelKey>(availableFields);
        return this.allMetricOptions.filter((metric) => availableMetrics.has(metric));
    }

    get isValid(): boolean {
        const metrics = [...(this.widget.primaryMetrics || []), ...(this.widget.secondaryMetrics || [])].filter(Boolean);
        return (
            !!this.widget.projectDeviceId &&
            this.widget.primaryMetrics.length >= 1 &&
            this.widget.primaryMetrics.length <= 2 &&
            this.widget.secondaryMetrics.length <= 4 &&
            new Set(metrics).size === metrics.length
        );
    }

    private get selectedDevice(): ProjectDeviceModel | undefined {
        return this.data.devices.find((device) => device.id === this.widget.projectDeviceId);
    }

    onDeviceChange(): void {
        this.pruneUnavailableSelections();
    }

    save(): void {
        if (!this.isValid) {
            return;
        }

        this.dialogRef.close({
            ...this.widget,
            name: this.widget.name?.trim() || "",
        } as CustomWidgetModel);
    }

    cancel(): void {
        this.dialogRef.close();
    }

    private pruneUnavailableSelections(): void {
        const availableMetrics = new Set(this.metricOptions);
        this.widget.primaryMetrics = (this.widget.primaryMetrics || []).filter((metric) =>
            availableMetrics.has(metric),
        );
        this.widget.secondaryMetrics = (this.widget.secondaryMetrics || []).filter((metric) =>
            availableMetrics.has(metric),
        );

        if (this.widget.chartMetric && !availableMetrics.has(this.widget.chartMetric)) {
            this.widget.chartMetric = null;
        }

        if (!this.widget.primaryMetrics.length && this.metricOptions.length) {
            this.widget.primaryMetrics = [this.metricOptions[0]];
        }
    }
}
